import React, { useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  SwapVertRounded as SortIcon,
  CheckRounded as CheckIcon,
  ArrowUpwardRounded as AscIcon,
  ArrowDownwardRounded as DescIcon,
} from "@mui/icons-material";

/**
 * The sort control every pawn report carries next to its View button, plus the
 * comparator the reports run their rows through.
 *
 * One button opens one menu: pick WHAT to sort on (each report supplies its own
 * field list — date, amount, customer, voucher no. — because the same four ideas
 * live under different keys on each report), then pick ascending or descending.
 * "Default order" puts the report back to the order it was fetched in.
 *
 * The sort is purely client-side: it reorders the rows already on screen, so it
 * never re-hits the network and never changes which records are shown (or what
 * the totals add up to).
 */

const BLUE = "#2563EB";
const BLUE_PALE = "#EFF6FF";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";

/** The "untouched" sort: rows stay in the order the report built them. */
export const DEFAULT_SORT = { key: null, dir: "asc" };

// A cell with nothing in it — including the em dash the reports print for a
// missing value, which would otherwise sort as a character.
const isBlank = (v) => v == null || v === "" || v === "—" || v === "…";

// Compare two strings the way a human reads a voucher number: digit runs
// compare as numbers, so PT9 lands before PT10 instead of after it. ISO dates
// ("2026-08-03") fall out of the same rule correctly.
const CHUNKS = /\d+|\D+/g;
const naturalCompare = (a, b) => {
  const ax = a.toLowerCase().match(CHUNKS) || [];
  const bx = b.toLowerCase().match(CHUNKS) || [];
  const n = Math.min(ax.length, bx.length);
  for (let i = 0; i < n; i++) {
    const s1 = ax[i];
    const s2 = bx[i];
    const bothNumeric = /^\d/.test(s1) && /^\d/.test(s2);
    const d = bothNumeric
      ? Number(s1) - Number(s2)
      : s1 < s2
        ? -1
        : s1 > s2
          ? 1
          : 0;
    if (d) return d;
  }
  return ax.length - bx.length;
};

/** Numbers numerically, everything else naturally. */
export const compareValues = (a, b) => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return naturalCompare(String(a), String(b));
};

/**
 * Reorder `rows` by the active sort.
 *
 * `accessors` maps a sort key to the value on the row it sorts by; a key the
 * report can't sort on is simply left out of the map and the rows come back
 * untouched — that is how the Summary / Floor-wise layouts share one control
 * with the record table without having to hide options.
 *
 * Blank cells always sink to the bottom (in BOTH directions) — a run of dashes
 * at the top of a descending sort tells the reader nothing — and equal values
 * keep their fetched order, so the sort is stable.
 */
export const sortRows = (rows, sort, accessors) => {
  const get = sort?.key ? accessors?.[sort.key] : null;
  if (!get || !Array.isArray(rows) || rows.length < 2) return rows;
  const sign = sort.dir === "desc" ? -1 : 1;
  return rows
    .map((row, i) => ({ row, i }))
    .sort((x, y) => {
      const a = get(x.row);
      const b = get(y.row);
      const aBlank = isBlank(a);
      const bBlank = isBlank(b);
      if (aBlank || bBlank) {
        if (aBlank && bBlank) return x.i - y.i;
        return aBlank ? 1 : -1;
      }
      const c = compareValues(a, b) * sign;
      return c !== 0 ? c : x.i - y.i;
    })
    .map((x) => x.row);
};

const captionSx = {
  px: 2,
  pt: 1,
  pb: 0.5,
  fontSize: "0.65rem",
  fontWeight: 700,
  color: INK_MUTED,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const itemSx = {
  fontSize: "0.82rem",
  color: INK_SOFT,
  py: 0.6,
  minHeight: 0,
};

/**
 * @param options {{ key: string, label: string }[]} the fields this report sorts on
 * @param value   {{ key: string|null, dir: "asc"|"desc" }}
 * @param onChange called with the next sort
 */
const ReportSortControl = ({ options = [], value, onChange, disabled }) => {
  const [anchor, setAnchor] = useState(null);
  const sort = value || DEFAULT_SORT;
  const active = !!sort.key;
  const activeLabel = options.find((o) => o.key === sort.key)?.label;

  const close = () => setAnchor(null);
  const pickField = (key) => {
    // Tapping the field that is already on turns the sort off again.
    onChange(key === sort.key ? { ...DEFAULT_SORT, dir: sort.dir } : { key, dir: sort.dir });
    close();
  };
  const pickDir = (dir) => {
    onChange({ ...sort, dir });
    close();
  };

  const check = (on) => (
    <ListItemIcon sx={{ minWidth: 28 }}>
      {on ? <CheckIcon sx={{ fontSize: 18, color: BLUE }} /> : null}
    </ListItemIcon>
  );

  return (
    <>
      <Tooltip
        title={
          active
            ? `Sorted by ${activeLabel} (${
                sort.dir === "desc" ? "descending" : "ascending"
              })`
            : "Sort records"
        }
      >
        {/* A span keeps the tooltip working while the button is disabled. */}
        <Box component="span" sx={{ display: "inline-flex" }}>
          <IconButton
            onClick={(e) => setAnchor(e.currentTarget)}
            disabled={disabled}
            sx={{
              border: `1px solid ${active ? BLUE : BORDER}`,
              borderRadius: 2,
              bgcolor: active ? BLUE_PALE : "transparent",
            }}
          >
            <SortIcon sx={{ color: active ? BLUE : INK_MUTED }} />
          </IconButton>
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 210, borderRadius: 2 } }}
      >
        <Typography sx={captionSx}>Sort by</Typography>
        {options.map((o) => (
          <MenuItem key={o.key} onClick={() => pickField(o.key)} sx={itemSx}>
            {check(sort.key === o.key)}
            <ListItemText
              primaryTypographyProps={{ fontSize: "0.82rem" }}
              primary={o.label}
            />
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />
        <Typography sx={captionSx}>Order</Typography>
        {[
          { dir: "asc", label: "Ascending", Icon: AscIcon },
          { dir: "desc", label: "Descending", Icon: DescIcon },
        ].map(({ dir, label, Icon }) => (
          <MenuItem key={dir} onClick={() => pickDir(dir)} sx={itemSx}>
            {check(sort.dir === dir)}
            <Icon sx={{ fontSize: 16, color: INK_MUTED, mr: 0.75 }} />
            <ListItemText
              primaryTypographyProps={{ fontSize: "0.82rem" }}
              primary={label}
            />
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          disabled={!active}
          onClick={() => {
            onChange(DEFAULT_SORT);
            close();
          }}
          sx={{ ...itemSx, pl: 2 }}
        >
          <ListItemText
            primaryTypographyProps={{ fontSize: "0.82rem", color: INK_MUTED }}
            primary="Default order"
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default ReportSortControl;
